import { useEffect, useState } from 'react';
import { postContact, ApiError } from '../api/client';

interface ContactModalProps {
  onClose: () => void;
}

const SUBJECT_MAX = 100;
const MESSAGE_MAX = 1000;
const NAME_MAX = 80;

/**
 * Formulário "Falar com os desenvolvedores" em modal. Fecha com Esc ou clique
 * fora (a menos que esteja enviando). Reaproveita as classes .modal-overlay /
 * .modal e os estilos de campo/botão do tema.
 */
export function ContactModal({ onClose }: ContactModalProps): React.JSX.Element {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !sending) onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, sending]);

  const canSubmit =
    name.trim().length > 0 &&
    subject.trim().length > 0 &&
    message.trim().length > 0 &&
    !sending;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    setError(null);
    try {
      const trimmedEmail = email.trim();
      await postContact({
        name: name.trim(),
        subject: subject.trim(),
        message: message.trim(),
        // Only include email when provided (exactOptionalPropertyTypes).
        ...(trimmedEmail ? { email: trimmedEmail } : {}),
      });
      setSent(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível enviar a mensagem.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={() => {
        if (!sending) onClose();
      }}
      role="presentation"
    >
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label="Falar com os desenvolvedores"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ position: 'relative' }}>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label="Fechar"
            style={{ position: 'absolute', top: 12, right: 12 }}
            disabled={sending}
          >
            ×
          </button>

          {sent ? (
            <div className="contact-sent">
              <h2>Mensagem enviada!</h2>
              <p className="modal-desc">
                Obrigado pelo contato. Vamos ler sua mensagem em breve.
              </p>
              <div className="contact-actions">
                <button type="button" className="btn btn-primary" onClick={onClose}>
                  Fechar
                </button>
              </div>
            </div>
          ) : (
            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <h2>Falar com os desenvolvedores</h2>
              <p className="modal-desc" style={{ color: 'var(--text-dim)' }}>
                Dúvidas, sugestões ou problemas? Escreva pra gente.
              </p>

              <label className="contact-field">
                <span>Nome</span>
                <input
                  className="search-input"
                  type="text"
                  value={name}
                  maxLength={NAME_MAX}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoFocus
                />
              </label>

              <label className="contact-field">
                <span>E-mail (opcional)</span>
                <input
                  className="search-input"
                  type="email"
                  value={email}
                  maxLength={120}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="para respondermos você"
                />
              </label>

              <label className="contact-field">
                <span>
                  Assunto
                  <em className="contact-count">
                    {subject.length}/{SUBJECT_MAX}
                  </em>
                </span>
                <input
                  className="search-input"
                  type="text"
                  value={subject}
                  maxLength={SUBJECT_MAX}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                />
              </label>

              <label className="contact-field">
                <span>
                  Mensagem
                  <em className="contact-count">
                    {message.length}/{MESSAGE_MAX}
                  </em>
                </span>
                <textarea
                  className="search-input contact-textarea"
                  value={message}
                  maxLength={MESSAGE_MAX}
                  rows={5}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </label>

              {error && <p className="contact-error">{error}</p>}

              <div className="contact-actions">
                <button type="button" className="btn" onClick={onClose} disabled={sending}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
                  {sending ? 'Enviando…' : 'Enviar'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
